import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} StackedSlidersRefs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [panels]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement} [counter]
 */

/**
 * @typedef {'proximity' | 'mandatory'} SnapMode
 */

/**
 * The stacked sliders scroll on the document scroller, so scroll snapping has to be requested on
 * `<html>`. Requests are pooled: several sections can be on the same page, and the strongest
 * request wins for as long as it is held.
 *
 * @type {Map<StackedSliders, SnapMode>}
 */
const snapRequests = new Map();

/**
 * Whether the document scroll is locked by an open drawer or dialog. Gestures on those
 * must not be taken over: paging scrolls with `scrollTo`, which `overflow: hidden`
 * cannot block, so the page would move behind the overlay.
 *
 * @returns {boolean} True while a scroll-locking overlay is open.
 */
function scrollLocked() {
  return (
    document.documentElement.hasAttribute('scroll-lock') ||
    !!document.querySelector('details[scroll-lock][open], dialog[open]')
  );
}

function syncSnapMode() {
  const modes = new Set(snapRequests.values());
  const mode = modes.has('mandatory') ? 'mandatory' : modes.has('proximity') ? 'proximity' : null;

  if (mode) {
    document.documentElement.dataset.stackedSnap = mode;
  } else {
    delete document.documentElement.dataset.stackedSnap;
  }
}

/** Wheel events further apart than this, in milliseconds, belong to different gestures. */
const WHEEL_GESTURE_GAP = 200;

/** Wheel travel a gesture has to accumulate, in pixels, before it pages. */
const WHEEL_PAGE_THRESHOLD = 50;

/** Weight of the latest wheel event in the running average of the gesture's travel. */
const WHEEL_SMOOTHING = 0.3;

/** Wheel travel below this, in pixels, is too small to say anything about the gesture. */
const WHEEL_NOISE_FLOOR = 8;

/** How long the scroll has to be still, in milliseconds, before the stack settles onto a panel. */
const SETTLE_DELAY = 150;

/**
 * The vertical travel of a wheel event, in pixels. Browsers may report it in lines or pages
 * instead — Firefox does for a mouse wheel — and those would never add up to a pixel threshold.
 *
 * @param {WheelEvent} event - The wheel event.
 * @returns {number} The vertical delta, in pixels.
 */
function wheelDeltaY(event) {
  switch (event.deltaMode) {
    case WheelEvent.DOM_DELTA_LINE:
      return event.deltaY * 16;
    case WheelEvent.DOM_DELTA_PAGE:
      return event.deltaY * window.innerHeight;
    default:
      return event.deltaY;
  }
}

/**
 * A stack of full-bleed panels that reveal one another on scroll.
 *
 * The stacking itself is pure CSS: every panel is `position: sticky` inside a tall track, so the
 * panel below slides up over the pinned panel above it without the scroll ever being hijacked.
 * This component only adds the parts CSS can't express: how far each panel has been covered
 * (`--stacked-progress`, which drives the optional scale/dim/parallax), which panel is active,
 * pagination state, and document scroll snapping while the section is on screen.
 *
 * @extends {Component<StackedSlidersRefs>}
 */
class StackedSliders extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {number | undefined} */
  #frame;
  /** Document offset of the first panel's snap position. */
  #start = 0;
  /** Scroll distance between two panels, i.e. one panel height. */
  #step = 0;
  /** Document offset and height of the stack, used to work out how much of the screen it fills. */
  #trackTop = 0;
  #trackHeight = 0;
  /** Offset the panels are pinned at: 0 when they pin to the top of the viewport. */
  #offset = 0;
  /** @type {HTMLElement | null} The element revealed over the last panel, if that setting is on. */
  #revealedElement = null;
  /** @type {HTMLElement | null} The revealed element currently being observed for size changes. */
  #observedRevealed = null;
  #active = -1;
  #onScreen = false;
  /** Whether the page has finished loading, after which upcoming videos may start buffering. */
  #pageLoaded = false;
  /** @type {SnapMode | null} */
  #snapMode = null;
  /** Snapping is released while a programmatic scroll is in flight. */
  #suppressSnap = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #releaseTimer;
  /** @type {number | undefined} Frame of the panel-to-panel animation in `smooth` mode. */
  #pageFrame;
  /** A move asked for while another was still animating, played once that one lands: 1, -1 or 0. */
  #pendingDirection = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} Waits for a scroll to come to rest. */
  #settleTimer;
  /** @type {number | null} Where the current touch gesture started. */
  #touchStart = null;
  /** Whether the current touch gesture has already paged: one swipe moves exactly one panel. */
  #touchSpent = false;
  /** Accumulated travel of the current wheel gesture, in pixels. */
  #wheelDelta = 0;
  /** Timestamp of the last wheel event. */
  #wheelLast = 0;
  /** Direction of the current wheel gesture: 1 down, -1 up. */
  #wheelDirection = 0;
  /** Running average of the current wheel gesture's travel per event, in pixels. */
  #wheelAverage = 0;
  /** The highest the running average has been during the current wheel gesture. */
  #wheelPeak = 0;
  /** The lowest the running average has been since the current wheel gesture started coasting. */
  #wheelTrough = 0;
  /** Whether the current wheel gesture has tailed off into trackpad momentum. */
  #wheelCoasting = false;
  /** Whether the current wheel gesture has already paged: one flick moves exactly one panel. */
  #wheelSpent = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    // Scrolls the section doesn't drive itself still have to end on a panel.
    window.addEventListener('scroll', this.#scheduleSettle, { passive: true, signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    // `smooth` mode takes the gesture over and animates one panel at a time, so these have to be
    // cancellable.
    window.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    window.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    window.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
    window.addEventListener('keydown', this.#handleKeydown, { signal });

    // Upcoming panel videos only start buffering once everything else on the page has arrived, so
    // they never compete with the opening panel.
    if (document.readyState === 'complete') {
      this.#pageLoaded = true;
    } else {
      window.addEventListener('load', this.#handlePageLoad, { once: true, signal });
    }

    // The panel height is expressed in `dvh`, so it changes on resize (including the mobile
    // browser chrome retracting), and the panels can still grow when blocks inside them hydrate.
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.refs.track);

    // Scroll work only runs while the section is on screen.
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;

        this.#onScreen = entry.isIntersecting;
        this.#measure();
        this.#update();
      },
      { threshold: 0 }
    );
    this.#intersectionObserver.observe(this);

    this.#measure();
    this.#observeRevealed();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#observedRevealed = null;

    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#pageFrame) cancelAnimationFrame(this.#pageFrame);
    clearTimeout(this.#releaseTimer);
    clearTimeout(this.#settleTimer);

    snapRequests.delete(this);
    syncSnapMode();
  }

  updatedCallback() {
    super.updatedCallback();

    this.#active = -1;
    this.#measure();
    this.#observeRevealed();
    this.#update();
  }

  /** @returns {HTMLElement[]} The panels, in document order. */
  get panels() {
    return this.refs.panels ?? [];
  }

  /**
   * Scrolls the panel at `index` into place. Used by the pagination and by the theme editor.
   *
   * @param {number | string} index - The zero-based index of the panel.
   * @param {Event} [event] - The event that triggered the selection.
   */
  select(index, event) {
    event?.preventDefault();

    const target = Number(index);
    if (Number.isNaN(target)) return;

    this.#measure();

    if (!this.#step) return;

    // Scroll snapping and an animated scroll fight each other — the animation crawls while the
    // scroller keeps being pulled back to the snap position it started from. Let go of snapping
    // until this scroll has landed.
    this.#releaseSnap();

    window.scrollTo({
      top: this.#start + clamp(target, 0, this.panels.length - 1) * this.#step,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Drops this section's snapping claim until the current scroll comes to a stop. `scrollend` isn't
   * available everywhere yet, so a timer backs it up.
   */
  #releaseSnap() {
    this.#suppressSnap = true;
    this.#snapMode = null;
    snapRequests.delete(this);
    syncSnapMode();

    const restore = () => {
      window.removeEventListener('scrollend', restore);
      clearTimeout(this.#releaseTimer);

      this.#suppressSnap = false;
      this.#requestUpdate();
    };

    clearTimeout(this.#releaseTimer);
    window.addEventListener('scrollend', restore);
    this.#releaseTimer = setTimeout(restore, 1500);
  }

  /**
   * Measures the sizes of the stack.
   *
   * Panels are equally tall and laid out one after another, so a single step is enough to know
   * where every panel pins. `top` is read from the first panel because the panels can be pinned
   * to the top of the viewport or centered in it, depending on the section settings. None of this
   * changes while scrolling, only when the viewport or the panels themselves change.
   */
  #measure() {
    const [first] = this.panels;

    if (!first) {
      this.#step = 0;
      return;
    }

    this.#offset = parseFloat(getComputedStyle(first).top) || 0;
    this.#step = first.offsetHeight;

    this.#syncRelease();

    this.#trackHeight = this.refs.track.offsetHeight;

    this.#measureBounds();
  }

  /**
   * Sets up the "reveal next section over the last panel" overlap.
   *
   * The last panel is held in place by a spacer at the end of the track, and the content below is
   * pulled up by the same amount so the page keeps its natural length. That amount is the height of
   * the content being revealed, capped at one panel: overlapping by more than the revealed content
   * is tall would leave it stranded mid-screen at the bottom of the page, and the spacer's own
   * overflow would add scroll length that leads nowhere.
   */
  #syncRelease() {
    const wrapper = this.closest('.stacked-sliders-section');

    if (!(wrapper instanceof HTMLElement)) return;

    const next = this.#revealed(wrapper);
    const height = next && this.hasAttribute('data-reveal-next') ? Math.min(next.offsetHeight, this.#step) : 0;
    const margin = height ? `${-height}px` : '';

    this.#revealedElement = next;

    // Only write when it actually changes: this runs from a ResizeObserver that watches the very
    // elements being resized.
    if (this.style.getPropertyValue('--stacked-release') !== `${height}px`) {
      this.style.setProperty('--stacked-release', `${height}px`);
    }

    if (wrapper.style.marginBlockEnd !== margin) {
      wrapper.style.marginBlockEnd = margin;
    }
  }

  /**
   * The element that slides up over the last panel: the next section, or whatever follows `main`
   * (the footer, usually) when this is the last section on the page.
   *
   * @param {HTMLElement} wrapper - The section wrapper.
   * @returns {HTMLElement | null} The element being revealed.
   */
  #revealed(wrapper) {
    const parent = wrapper.parentElement;
    const scope = parent?.tagName === 'MAIN' && parent.lastElementChild === wrapper ? parent : wrapper;

    let next = scope.nextElementSibling;

    while (next) {
      if (next instanceof HTMLElement && next.offsetHeight > 0) return next;

      next = next.nextElementSibling;
    }

    return null;
  }

  /**
   * Locates the stack in the document. This is re-read on every update because anything above the
   * section — images finishing loading, fonts swapping, a section being re-rendered — moves it.
   */
  #measureBounds() {
    this.#trackTop = this.refs.track.getBoundingClientRect().top + window.scrollY;
    this.#start = this.#trackTop - this.#offset;
  }

  /**
   * How much of the screen the stack currently fills, from 0 to 1.
   *
   * @returns {number} The share of the viewport the stack covers.
   */
  #coverage() {
    const viewport = window.innerHeight;

    if (!viewport || !this.#trackHeight) return 0;

    const top = Math.max(window.scrollY, this.#trackTop);
    const bottom = Math.min(window.scrollY + viewport, this.#trackTop + this.#trackHeight);

    return clamp((bottom - top) / viewport, 0, 1);
  }

  #requestUpdate = () => {
    if (this.#frame || !this.#onScreen) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #handleResize = () => {
    this.#measure();
    this.#observeRevealed();
    this.#update();
  };

  /**
   * Keeps an eye on the height of the element revealed over the last panel, since the overlap is
   * derived from it.
   */
  #observeRevealed() {
    const element = this.#revealedElement;

    if (!this.#resizeObserver || element === this.#observedRevealed) return;

    if (this.#observedRevealed) this.#resizeObserver.unobserve(this.#observedRevealed);
    if (element) this.#resizeObserver.observe(element);

    this.#observedRevealed = element;
  }

  #update() {
    const panels = this.panels;

    if (!panels.length || !this.#step) return;

    // Read layout first, write after: everything below only sets custom properties and attributes.
    this.#measureBounds();

    const position = (window.scrollY - this.#start) / this.#step;

    // Per-panel progress is only worth writing when an effect actually reads it — setting a custom
    // property on a panel invalidates the styles of everything inside it.
    if (this.hasAttribute('data-effects')) {
      for (const [index, panel] of panels.entries()) {
        // How far this panel has been covered by the one after it: 0 while it owns the viewport,
        // 1 once the next panel sits completely on top of it.
        const progress = clamp(position - index, 0, 1);

        panel.style.setProperty('--stacked-progress', progress.toFixed(4));
      }
    }

    const active = clamp(Math.round(position), 0, panels.length - 1);
    const total = panels.length > 1 ? clamp(position / (panels.length - 1), 0, 1) : 1;
    const coverage = this.#coverage();

    this.style.setProperty('--stacked-total-progress', total.toFixed(4));

    // Keep the pagination out of the way until the stack fills most of the screen, so it doesn't
    // hover over the section above while the first panel is still sliding in.
    this.toggleAttribute('data-ui-hidden', coverage < 0.8);
    this.toggleAttribute('data-scrolled', position > 0.02);

    if (active !== this.#active) {
      this.#active = active;
      this.#syncActive(active);
      this.#syncMedia(active);
    }

    this.#syncSnapRequest(position, coverage);
  }

  /**
   * @param {number} active - The index of the panel that currently owns the viewport.
   */
  #syncActive(active) {
    this.dataset.activeIndex = String(active);

    for (const [index, dot] of (this.refs.dots ?? []).entries()) {
      dot.setAttribute('aria-selected', String(index === active));
    }

    const { counter } = this.refs;
    if (counter) counter.textContent = String(active + 1);
  }

  #handlePageLoad = () => {
    this.#pageLoaded = true;
    if (this.#active >= 0) this.#syncMedia(this.#active);
  };

  /**
   * Plays the video of the panels around the active one and pauses the rest, so panels that are
   * buried under the stack aren't decoding frames nobody can see.
   *
   * With lazy loading on (Theme settings → Media loading), later panels' videos render with
   * `preload="none"` and download nothing until played. The neighbours already play, so to keep a
   * quick swipe from outrunning the download, the panel two ahead also buffers (without playing)
   * once the page has loaded.
   *
   * @param {number} active - The index of the panel that currently owns the viewport.
   */
  #syncMedia(active) {
    for (const [index, panel] of this.panels.entries()) {
      const video = panel.querySelector('video');

      if (!video) continue;

      if (Math.abs(index - active) <= 1) {
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }

      if (this.#pageLoaded && index === active + 2 && video.preload === 'none') {
        video.preload = 'auto';
        // Browsers don't all start fetching on the attribute change alone; `load()` on a video
        // that has nothing yet just restarts its (empty) resource selection with the new preload.
        if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
      }
    }
  }

  /**
   * Whether this section should take gestures over and move panel by panel. Only in `smooth` mode,
   * and only once the stack owns the screen — while it is still sliding into view the page should
   * scroll normally.
   *
   * @returns {boolean} True when gestures should be paged.
   */
  #paging() {
    return (
      this.dataset.snap === 'smooth' &&
      this.#step > 0 &&
      this.panels.length > 1 &&
      this.#coverage() > 0.8 &&
      !scrollLocked()
    );
  }

  /** @returns {number} The current scroll position, measured in panels. */
  #position() {
    return this.#step ? (window.scrollY - this.#start) / this.#step : 0;
  }

  /**
   * Whether there is another panel to move to. At either end the gesture is left alone so the
   * visitor can carry on up or down the page.
   *
   * @param {number} direction - 1 when moving down, -1 when moving up.
   * @returns {boolean} True when a panel in that direction exists.
   */
  #canPage(direction) {
    const position = this.#position();

    return direction > 0 ? position < this.panels.length - 1.02 : position > 0.02;
  }

  /**
   * Moves one panel in the given direction.
   *
   * A move asked for while the previous one is still animating is kept and played once that one
   * lands, so a quick pair of flicks moves two panels instead of losing the second.
   *
   * @param {number} direction - 1 when moving down, -1 when moving up.
   */
  #page(direction) {
    if (this.#pageFrame) {
      this.#pendingDirection = direction;
      return;
    }

    this.#pendingDirection = 0;

    const target = clamp(Math.round(this.#position()) + direction, 0, this.panels.length - 1);

    this.#animateTo(this.#start + target * this.#step);
  }

  /** Plays the move queued while the last animation ran, if there is still somewhere to go. */
  #flushPending() {
    const direction = this.#pendingDirection;

    this.#pendingDirection = 0;

    if (direction && this.#paging() && this.#canPage(direction)) this.#page(direction);
  }

  /**
   * Scrolls to a position over a fixed, snappy duration. `scroll-behavior: smooth` is duration-less
   * and eases differently per distance, so the animation is driven here instead.
   *
   * @param {number} top - The scroll position to land on.
   */
  #animateTo(top) {
    const from = window.scrollY;
    const distance = top - from;

    if (!distance) return;

    // A hidden tab gets no animation frames, so an animation started there would never finish and
    // would swallow every gesture after it.
    if (prefersReducedMotion() || document.hidden) {
      window.scrollTo({ top, behavior: 'instant' });
      return;
    }

    const duration = Number(this.dataset.snapSpeed) || 600;
    const started = performance.now();

    const tick = (now) => {
      // Frames stop coming if the tab is hidden part way through: land on the target instead of
      // leaving the page parked between two panels.
      if (document.hidden) {
        window.scrollTo({ top, behavior: 'instant' });
        this.#pageFrame = undefined;
        this.#pendingDirection = 0;
        return;
      }

      const elapsed = clamp((now - started) / duration, 0, 1);
      // easeOutCubic: leaves immediately at full pace, settles without a bounce.
      const eased = 1 - Math.pow(1 - elapsed, 3);

      window.scrollTo({ top: from + distance * eased, behavior: 'instant' });

      if (elapsed < 1) {
        this.#pageFrame = requestAnimationFrame(tick);
        return;
      }

      this.#pageFrame = undefined;
      this.#flushPending();
    };

    this.#pageFrame = requestAnimationFrame(tick);
  }

  /**
   * Lands the stack on a panel once a scroll the section didn't drive has come to rest — a fling
   * Chromium kept for itself, a scrollbar drag, a smooth scroll cut short — so the page is never
   * left parked halfway between two panels.
   */
  #scheduleSettle = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, SETTLE_DELAY);
  };

  #settle = () => {
    if (this.#pageFrame || !this.#paging()) return;

    this.#measureBounds();

    const position = this.#position();

    // Only between the first and last panel: beyond either, whatever sits above or below the stack
    // is in charge of the scroll.
    if (position <= 0 || position >= this.panels.length - 1) return;

    const target = this.#start + Math.round(position) * this.#step;

    if (Math.abs(target - window.scrollY) <= 1) return;

    this.#animateTo(target);
  };

  /** @param {WheelEvent} event - The wheel event. */
  #handleWheel = (event) => {
    if (event.ctrlKey || !this.#paging()) return;

    // Ties go to vertical: the first event of a trackpad swipe is tiny and often diagonal, and once
    // a gesture's first event goes uncancelled Chromium hands the rest of it to native scrolling.
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    const delta = wheelDeltaY(event);

    if (!delta) return;

    const direction = delta > 0 ? 1 : -1;

    if (!this.#canPage(direction)) return;

    // A gesture Chromium has already handed to native scrolling can't be taken over: animating
    // against its fling would park the page between two panels. Let it run — the scroll settles
    // onto the nearest panel once it stops.
    if (!event.cancelable) return;

    // Swallow the gesture even while an animation is running, otherwise the page jumps ahead of it.
    event.preventDefault();

    this.#trackWheel(direction, Math.abs(delta));

    // Accumulating up to a threshold keeps a stray notch or a light trackpad touch from paging.
    if (this.#wheelSpent || this.#wheelDelta < WHEEL_PAGE_THRESHOLD) return;

    this.#wheelSpent = true;
    this.#page(direction);
  };

  /**
   * Folds a wheel event into the current gesture, or starts a new one.
   *
   * Wheel events have no begin/end, so gestures are inferred from the stream. A pause or a change
   * of direction plainly starts a new one. A trackpad also keeps sending events for a second or
   * more after the fingers lift — momentum, which only ever shrinks — so travel that grows again
   * once the gesture has tailed off is the next swipe, not the last one still coasting. Without
   * that, every swipe made before the previous tail died out would be swallowed.
   *
   * Single events are too noisy to judge that by: a trackpad reports travel in uneven bursts, and
   * the browser merges two inputs into one bigger delta whenever a frame is dropped. The gesture
   * is read through a running average instead, and only counts as a new swipe once that average
   * has clearly decayed and then doubles off its low — momentum can't do that. Each gesture pages
   * at most once, so a single swipe never skips over a panel.
   *
   * @param {number} direction - 1 when moving down, -1 when moving up.
   * @param {number} magnitude - The travel of the event, in pixels.
   */
  #trackWheel(direction, magnitude) {
    const now = performance.now();
    const paused = now - this.#wheelLast > WHEEL_GESTURE_GAP;
    const reversed = direction !== this.#wheelDirection;

    // A tiny event against the grain is noise, not a reversal.
    if (reversed && !paused && magnitude < WHEEL_NOISE_FLOOR) return;

    let average = this.#wheelAverage + (magnitude - this.#wheelAverage) * WHEEL_SMOOTHING;

    const fresh =
      paused ||
      reversed ||
      (this.#wheelCoasting && average >= WHEEL_NOISE_FLOOR && average > this.#wheelTrough * 2);

    if (fresh) {
      average = magnitude;

      this.#wheelDirection = direction;
      this.#wheelDelta = 0;
      this.#wheelPeak = 0;
      this.#wheelTrough = 0;
      this.#wheelCoasting = false;
      this.#wheelSpent = false;
    }

    this.#wheelLast = now;
    this.#wheelAverage = average;
    this.#wheelDelta += magnitude;

    if (average > this.#wheelPeak) {
      this.#wheelPeak = average;
    } else if (!this.#wheelCoasting) {
      // Momentum has clearly set in once the travel has shrunk to half the gesture's peak.
      if (average < this.#wheelPeak / 2) {
        this.#wheelCoasting = true;
        this.#wheelTrough = average;
      }
    } else if (average < this.#wheelTrough) {
      this.#wheelTrough = average;
    }
  }

  /** @param {TouchEvent} event - The touch event. */
  #handleTouchStart = (event) => {
    this.#touchStart = event.touches[0]?.clientY ?? null;
    this.#touchSpent = false;
  };

  /** @param {TouchEvent} event - The touch event. */
  #handleTouchMove = (event) => {
    const y = event.touches[0]?.clientY;

    if (y == null || event.touches.length > 1 || !this.#paging()) return;

    // One page per swipe: once the gesture has paged, keep swallowing it so the finger trailing
    // on doesn't scroll the page underneath the animation or push it a second panel.
    if (this.#touchSpent) {
      event.preventDefault();
      return;
    }

    if (this.#touchStart == null) return;

    const travelled = this.#touchStart - y;

    if (travelled === 0) return;

    const direction = travelled > 0 ? 1 : -1;

    // At either end the gesture is left alone so the visitor can carry on up or down the page.
    if (!this.#canPage(direction)) {
      this.#touchStart = null;
      return;
    }

    // iOS only honors preventDefault before native scrolling has begun, so the gesture has to be
    // claimed on its very first movement — waiting for the swipe threshold would be too late and
    // the panels would free-scroll.
    event.preventDefault();

    if (Math.abs(travelled) < 24) return;

    this.#touchStart = null;
    this.#touchSpent = true;
    this.#page(direction);
  };

  /** @param {KeyboardEvent} event - The keyboard event. */
  #handleKeydown = (event) => {
    if (!this.#paging()) return;

    const down = event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ';
    const up = event.key === 'ArrowUp' || event.key === 'PageUp';

    if (!down && !up) return;

    const { target } = event;

    if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable]')) return;

    const direction = down ? 1 : -1;

    if (!this.#canPage(direction)) return;

    event.preventDefault();
    this.#page(direction);
  };

  /**
   * Claims document scroll snapping while the stack is on screen.
   *
   * `mandatory` snapping is only ever held *between* the first and last panel: the document
   * scroller cannot be scrolled past a mandatory snap position, so holding it at the edges of the
   * section would trap the visitor inside it.
   *
   * @param {number} position - The scroll position, in panels.
   * @param {number} coverage - The share of the screen the stack covers.
   */
  #syncSnapRequest(position, coverage) {
    const setting = this.dataset.snap;
    const panels = this.panels.length;
    /** @type {SnapMode | null} */
    let mode = null;

    // `smooth` mode moves the scroll itself, so CSS snapping would only fight it.
    const snaps = setting === 'gentle' || setting === 'strict';

    if (snaps && !this.#suppressSnap && coverage > 0 && panels > 1) {
      const inside = position > 0.01 && position < panels - 1.01;

      mode = setting === 'strict' && inside ? 'mandatory' : 'proximity';
    }

    if (mode === this.#snapMode) return;

    this.#snapMode = mode;

    if (mode) snapRequests.set(this, mode);
    else snapRequests.delete(this);

    syncSnapMode();
  }

  /**
   * In the theme editor, selecting a panel block should bring that panel into view.
   *
   * @param {Event} event - The `shopify:block:select` event.
   */
  #handleBlockSelect = (event) => {
    const { target } = event;

    if (!(target instanceof HTMLElement)) return;

    const index = this.panels.indexOf(target);

    if (index === -1) return;

    this.select(index);
  };
}

if (!customElements.get('stacked-sliders')) {
  customElements.define('stacked-sliders', StackedSliders);
}
